import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Snackbar, Alert } from '@mui/material';
import {
  UserPlus,
  Phone,
  CalendarDays,
  IndianRupee,
  Upload,
  CheckCircle2,
  Loader2,
  AlertCircle,
  FileText,
  Sparkles,
} from 'lucide-react';
import { userApiClient } from '../../api/HttpClient';

interface UserProfile {
  phone?: string;
  email?: string;
}

interface SubscriptionResponse {
  id: string;
}

interface AssignTenantDialogProps {
  open: boolean;
  onClose: () => void;
  bedId: string;
  onTenantAssigned: () => void;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

const AssignTenantDialog: React.FC<AssignTenantDialogProps> = ({
  open,
  onClose,
  bedId,
  onTenantAssigned,
}) => {
  const navigate = useNavigate();
  const [tenantName, setTenantName] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [joinDate, setJoinDate] = useState<string | null>(null);
  const [rentAmount, setRentAmount] = useState<number | ''>('');
  const [advanceAmount, setAdvanceAmount] = useState<number | ''>('');
  const [identityDocument, setIdentityDocument] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  const showSnackbar = (message: string, severity: typeof snackbarSeverity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    setError(null);
    try {
      const client = await userApiClient;
      const response = await client.post<SubscriptionResponse>('/api/pg_tenant/subscriptions/initiate/');
      const subscriptionId = response.id;
      if (!subscriptionId) {
        setError('Failed to get subscription ID from the server.');
        setUpgradeLoading(false);
        return;
      }

      const userProfile = await client.get<UserProfile>('/api/users/my-profile/');
      const userPhone = userProfile?.phone || '9999999999';
      const userEmail = userProfile?.email || 'your.email@example.com';

      let paymentHandled = false;
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        subscription_id: subscriptionId,
        name: 'AffordaNest Subscription',
        description: 'Monthly subscription for unlimited tenants',
        handler: async (razorpayResponse: any) => {
          paymentHandled = true;
          setUpgradeModalOpen(false);
          setUpgradeLoading(false);
          showSnackbar('Payment successful! Activating your subscription...', 'success');
          try {
            await client.post('/api/pg_tenant/subscriptions/verify/', {
              razorpay_subscription_id: razorpayResponse.razorpay_subscription_id,
            });
            showSnackbar('Subscription activated! You can now add more tenants.', 'success');
            onTenantAssigned();
          } catch {
            showSnackbar('Payment done but activation is taking longer. Please refresh in a moment.', 'warning');
          }
        },
        modal: {
          ondismiss: () => {
            setUpgradeLoading(false);
          },
        },
        prefill: { name: 'Your Name', email: userEmail, contact: userPhone },
        theme: { color: '#276859' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setUpgradeLoading(false);
    } catch {
      setError('Failed to initiate subscription.');
      setUpgradeLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!tenantName || !tenantPhone || !joinDate || rentAmount === '') {
      setError('All fields are required.');
      return;
    }
    if (rentAmount <= 0) {
      setError('Rent amount must be positive.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const client = await userApiClient;
      const formData = new FormData();
      formData.append('name', tenantName);
      formData.append('phone', tenantPhone);
      if (joinDate) formData.append('join_date', joinDate);
      formData.append('rent', rentAmount.toString());
      if (advanceAmount !== '') formData.append('advance_amount', advanceAmount.toString());
      if (identityDocument) formData.append('identity_document', identityDocument);

      await client.post(`/api/pg_tenant/longterm/beds/${bedId}/assign-tenant/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onTenantAssigned();
      onClose();
      setTenantName('');
      setTenantPhone('');
      setJoinDate(null);
      setRentAmount('');
      setAdvanceAmount('');
      setIdentityDocument(null);
    } catch (err: any) {
      if (err.status === 403) {
        setUpgradeModalOpen(true);
      } else {
        setError(err.message || 'Failed to assign tenant.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  return (
    <>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* ── Assign Tenant Dialog ── */}
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[480px] bg-card p-0 gap-0 overflow-hidden">
          {/* Header strip */}
          <div className="bg-primary px-6 py-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg font-semibold">
                <UserPlus className="h-5 w-5" />
                Assign Tenant
              </DialogTitle>
              <DialogDescription className="text-primary-foreground/80 text-sm mt-0.5">
                Bed ID: {bedId}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Error banner */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            {/* Tenant Details */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Tenant Details</p>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="tenantName">Full Name <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="tenantName"
                      placeholder="e.g. Rahul Sharma"
                      value={tenantName}
                      onChange={(e) => setTenantName(e.target.value)}
                      className="bg-background pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="tenantPhone">Phone Number <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="tenantPhone"
                      type="tel"
                      placeholder="e.g. 9876543210"
                      value={tenantPhone}
                      onChange={(e) => setTenantPhone(e.target.value)}
                      className="bg-background pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="joinDate">Join Date <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="joinDate"
                      type="date"
                      value={joinDate || ''}
                      onChange={(e) => setJoinDate(e.target.value)}
                      className="bg-background pl-9"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Financials */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Financials</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="rentAmount">Rent Amount (₹) <span className="text-red-500">*</span></Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="rentAmount"
                      type="number"
                      placeholder="e.g. 5000"
                      value={rentAmount}
                      onChange={(e) => setRentAmount(parseFloat(e.target.value) || '')}
                      className="bg-background pl-9"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="advanceAmount">Advance Amount (₹)</Label>
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="advanceAmount"
                      type="number"
                      placeholder="Optional"
                      value={advanceAmount}
                      onChange={(e) => setAdvanceAmount(parseFloat(e.target.value) || '')}
                      className="bg-background pl-9"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Identity Document */}
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Identity Document</p>
              <input
                accept="image/*,.pdf"
                className="hidden"
                id="identity-doc-upload"
                type="file"
                onChange={(e) =>
                  setIdentityDocument(e.target.files ? e.target.files[0] : null)
                }
              />
              <Label
                htmlFor="identity-doc-upload"
                className="flex items-center gap-2 w-full border border-dashed border-border rounded-lg px-4 py-3 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
              >
                <Upload className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground">
                  {identityDocument ? identityDocument.name : 'Upload ID proof (image or PDF)'}
                </span>
                {identityDocument && (
                  <FileText className="h-4 w-4 text-primary ml-auto shrink-0" />
                )}
              </Label>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border flex gap-2">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1">
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Assigning…
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Assign Tenant
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Upgrade to Pro Dialog ── */}
      <Dialog open={upgradeModalOpen} onOpenChange={() => setUpgradeModalOpen(false)}>
        <DialogContent className="sm:max-w-[420px] bg-card p-0 gap-0 overflow-hidden">
          <div className="bg-primary px-6 py-5 text-center">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-white/15 mb-3">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <DialogTitle className="text-white text-xl font-bold">Upgrade to Pro</DialogTitle>
            <p className="text-primary-foreground/80 text-sm mt-1">
              You've reached your free tenant limit
            </p>
          </div>

          <div className="px-6 py-5">
            {/* Price */}
            <div className="text-center mb-5">
              <p className="text-4xl font-bold text-foreground">
                ₹199
                <span className="text-base font-normal text-muted-foreground ml-1">/ month</span>
              </p>
            </div>

            {/* Features */}
            <ul className="space-y-2.5 mb-5">
              {[
                'Onboard up to 200 long-term tenants',
                'Everything in Free',
                'View 5 seller contacts in PG Marketplace',
                'Priority support',
              ].map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-sm text-foreground">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>

            {error && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 mb-4">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setUpgradeModalOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpgrade}
                disabled={upgradeLoading}
                className="flex-1"
              >
                {upgradeLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Processing…
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    Upgrade Now
                  </span>
                )}
              </Button>
            </div>
            <button
              onClick={() => { setUpgradeModalOpen(false); navigate('/subscription'); }}
              className="w-full mt-3 text-xs text-center text-muted-foreground hover:text-primary underline underline-offset-2"
            >
              See all plans
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssignTenantDialog;
